// DA-5090, proposal 4: the organisation profile Elliott draws on when it
// writes Roleplays. An admin gives the company website, Elliott reads a few of
// its pages and drafts a profile (about, products, customers, team, pricing),
// and the admin edits it and saves it. Only a saved profile is ever used, and
// it changes only when an admin drafts it again or edits it here.
//
// Shown only where Hiring is on (the profile is read by Hiring's drafts today).

import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { hiringApi, hiringMessage, type HiringOrgProfile } from '../../../../api/hiring';
import { useHiringAccess, useHiringOrg } from '../../../../queries/useHiring';
import { ActionButton, BODY, DANGER, FONT, INPUT_SX, TEXT, TEXTAREA_SX } from '../capability-setup/designSystem';

const MAX_TEXT = 8000;

const when = (ms: number | null) => (ms ? new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');

const OrgProfileCard: React.FC = () => {
  const access = useHiringAccess();
  const org = useHiringOrg();
  const [saved, setSaved] = useState<HiringOrgProfile | null>(null);
  const [website, setWebsite] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState<'draft' | 'save' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    if (!access.data) return;
    hiringApi
      .orgProfile(org)
      .then((p) => {
        setSaved(p);
        setWebsite(p.website);
        setText(p.text);
      })
      .catch(() => setSaved(null));
  }, [access.data, org]);

  if (!access.data) return null;
  const blocked = access.data.acknowledgementRequired;
  const changed = !saved || text.trim() !== saved.text.trim() || website.trim() !== saved.website.trim();

  const draft = async () => {
    setBusy('draft');
    setError(null);
    setNote(null);
    try {
      const r = await hiringApi.draftOrgProfile(website.trim(), org);
      setWebsite(r.website);
      setText(r.text);
      setNote(`Drafted from ${r.pagesRead.length} ${r.pagesRead.length === 1 ? 'page' : 'pages'} of ${r.website}. Check every line, edit anything that's wrong, then save. Nothing is used until you save.`);
    } catch (err) {
      setError(hiringMessage(err, "Elliott couldn't read that website just now."));
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    setBusy('save');
    setError(null);
    try {
      const p = await hiringApi.saveOrgProfile({ website: website.trim(), text: text.trim() }, org);
      setSaved(p);
      setNote(p.text ? 'Saved. Elliott uses this the next time it drafts an assessment.' : 'Cleared. Elliott will describe a made-up company until you add a profile.');
    } catch (err) {
      setError(hiringMessage(err, "The profile couldn't be saved."));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box sx={{ background: '#1C1C1C', borderRadius: '8px', p: '25px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <Box>
        <Typography sx={{ fontFamily: FONT, fontSize: 16, fontWeight: 600, color: TEXT }}>Organisation profile</Typography>
        <Typography sx={{ ...BODY, mt: '4px' }}>
          What Elliott knows about your company when it writes hiring roleplays: what you sell, to whom, your teams and
          pricing. With it, candidates get situations set at your company instead of a made-up one.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        <Box
          component="input"
          value={website}
          aria-label="Company website"
          placeholder="https://yourcompany.com"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWebsite(e.target.value)}
          sx={{ ...INPUT_SX, flex: 1, minWidth: 220 }}
        />
        <ActionButton
          onClick={draft}
          disabled={!website.trim() || busy !== null || blocked}
          reason={blocked ? 'Confirm the Hiring notice first' : undefined}
        >
          {busy === 'draft' ? 'Reading the website...' : text ? 'Draft again from the website' : 'Draft from the website'}
        </ActionButton>
      </Box>

      <Box
        component="textarea"
        rows={12}
        maxLength={MAX_TEXT}
        aria-label="Organisation profile"
        value={text}
        placeholder={'About the company\n...\n\nProducts and services\n...\n\nCustomers and market\n...\n\nTeam and roles\n...\n\nPricing\n...'}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setText(e.target.value)}
        sx={{ ...TEXTAREA_SX, minHeight: 220 }}
      />

      {note && <Typography sx={{ ...BODY, fontSize: 12.5 }}>{note}</Typography>}
      {error && (
        <Typography sx={{ ...BODY, color: DANGER }} role="alert">
          {error}
        </Typography>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Typography sx={{ ...BODY, fontSize: 12 }}>
          {saved?.approvedAt ? `Saved by ${saved.approvedBy || 'an admin'} on ${when(saved.approvedAt)}.` : 'Not saved yet, so Elliott is not using it.'}
        </Typography>
        <ActionButton primary onClick={save} disabled={!changed || busy !== null || blocked}>
          {busy === 'save' ? 'Saving...' : 'Save profile'}
        </ActionButton>
      </Box>
    </Box>
  );
};

export default OrgProfileCard;
